import React from 'react';
import { Box, Typography, Button, Card, CardContent, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StepFileSelectionProps } from '../types';
import { FolderInfoCard } from './FolderInfoCard';
import { FileSelectionTree } from './FileSelectionTree';

/**
 * Step 2: File selection wrapper
 * Integrates FolderInfoCard and FileSelectionTree
 * Handles loading and empty states
 */
export const StepFileSelection: React.FC<StepFileSelectionProps> = ({
  folderInfo,
  files,
  selectedFiles,
  onToggleFile,
  onSelectAll,
  onSelectNone,
  isLoading,
  mode,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {mode === 'edit' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('shareFolder.editMode.filesInfo')}
          </Typography>
        </Alert>
      )}

      {folderInfo && <FolderInfoCard folderInfo={folderInfo} />}

      {/* Loading State */}
      {isLoading && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {t('shareFolder.loadingFiles')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* No Files Found */}
      {!isLoading && files?.length === 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('shareFolder.noFilesFound')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* File Selection */}
      {files && files.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {mode === 'edit' ? t('shareFolder.selectFilesToSync') : t('shareFolder.selectFilesToImport')}
            </Typography>

            {/* Select All/None buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                size="small"
                onClick={onSelectAll}
                disabled={selectedFiles.length === files.length}
              >
                {t('shareFolder.selectAll')}
              </Button>
              <Button
                size="small"
                onClick={onSelectNone}
                disabled={selectedFiles.length === 0}
              >
                {t('shareFolder.selectNone')}
              </Button>
            </Box>

            {/* File Tree */}
            <FileSelectionTree
              files={files}
              selectedFiles={selectedFiles}
              onToggleFile={onToggleFile}
              maxHeight={400}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('shareFolder.filesSelected', { selected: selectedFiles.length, total: files.length })}
            </Typography>
          </CardContent>
        </Card>
      )}
    </>
  );
};
